/**
 * Korrekturen je Artikel in der Prüfansicht (Phase 3, 28.09.2026).
 *
 * Matze: „die Eigenschaften und Tags sollen doch dann auch gleich im Artikel einstellbar
 * sein, aber natürlich auch übergeordnet eine Maske für alle."
 *
 * - Gespeichert wird je Hersteller + Artikel — die Korrektur gilt in jedem neuen Entwurf.
 * - Am Artikel stehen nur ABWEICHUNGEN von der Maske. Was nicht geändert ist, folgt der Maske.
 * - Alles von Hand Geänderte trägt „von Hand" und lässt sich zurücksetzen.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, Loader2, Pencil, RotateCcw, Star, Trash2, EyeOff, Eye } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { euro, usd, zahl } from '../lib/format';
import { bloeckeZuHtml, bloeckeZuText, textZuBloecken, type Block } from '../lib/artikeltext';
import Info from './Info';
import KategorieWahl from './KategorieWahl';
import { Hinzufuegen, Wertfeld } from './RegelnPanel';
import type {
  AbgeleiteteEigenschaft, ArtikelPruefung, EigenschaftsKorrektur, EigenschaftsRegel, KatalogAntwort, KategorieKi, KategorieKiStatus, KorrekturBereich,
  PreisKorrektur, SchreibbildFeld,
} from '../lib/typen';

export type Speichere = (bereich: KorrekturBereich, wert: unknown) => Promise<void>;

// ==================== Bausteine ====================

/** Eigenschaft „Kaufland.de-Kategorie" — eigener Weg, nur an die Kindvarianten (09.10.2026). */
const KAUFLAND_KATEGORIE_ID = 107;

/**
 * Kaufland.de-Kategorie: Wert nach der Regel des Tools Kaufland-Kategorisierung (Name zuerst,
 * sonst Kategorie), von Hand änderbar oder „keine". Geschrieben wird sie NUR an die
 * Kindvarianten — die übrigen Eigenschaften erben die Kinder weiter von der Hauptvariante.
 */
function KauflandKarte({ kaufland, speichere }: { kaufland: NonNullable<ArtikelPruefung['kaufland']>; speichere: Speichere }) {
  const [arbeitet, setArbeitet] = useState(false);
  const wahl = kaufland.vonHand ? (kaufland.selectionId == null ? 'keine' : String(kaufland.selectionId)) : 'regel';
  const aendern = async (v: string) => {
    setArbeitet(true);
    try {
      if (v === 'regel') await speichere('kaufland', null);
      else if (v === 'keine') await speichere('kaufland', { selectionId: null });
      else await speichere('kaufland', { selectionId: Number(v) });
    } finally { setArbeitet(false); }
  };
  const ziel = kaufland.kindvarianten > 0
    ? (kaufland.kindvarianten === 1
      ? 'an die Kindvariante, nicht an die Hauptvariante'
      : `an die ${zahl(kaufland.kindvarianten)} Kindvarianten, nicht an die Hauptvariante`)
    : 'an die Variante (Artikel ohne Kindvarianten)';
  return (
    <Karte titel="Kaufland.de-Kategorie · nur an die Kindvarianten" zusatz={arbeitet && <Loader2 className="h-3.5 w-3.5 animate-spin" />}>
      {!kaufland.aktiv ? (
        <p className="text-sm text-[#6B6A66]">Bei dieser Marke wird keine Kaufland.de-Kategorie geschrieben.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={wahl}
              disabled={arbeitet}
              onChange={e => void aendern(e.target.value)}
              className="min-w-[16rem] rounded border border-[#DAD9D9] bg-white px-2 py-1 text-sm"
            >
              <option value="regel">Regel: {kaufland.regelWert ?? 'kein Wert'} ({kaufland.regelQuelleText})</option>
              <option value="keine">keine — nicht schreiben</option>
              {kaufland.auswahl.map(w => <option key={w.id} value={String(w.id)}>{w.wert}</option>)}
            </select>
            {kaufland.vonHand && <span className="rounded bg-[#E9FF57] px-2 py-0.5 text-xs text-[#1A1A1A]">von Hand</span>}
          </div>
          {kaufland.selectionId != null ? (
            <p className="text-xs text-[#6B6A66]">Wird geschrieben: <b className="text-[#1A1A1A]">{kaufland.wert}</b> ({kaufland.quelleText}) — {ziel}.</p>
          ) : (
            <p className="text-xs text-amber-900">Kein Kaufland-Wert ({kaufland.quelleText}) — wird nicht geschrieben.</p>
          )}
        </>
      )}
    </Karte>
  );
}

function Karte({ titel, zusatz, children, className = '' }: {
  titel: string; zusatz?: ReactNode; children: ReactNode; className?: string;
}) {
  return (
    <div className={`space-y-2 rounded-lg border border-[#DAD9D9] bg-white p-4 ${className}`}>
      <div className="flex items-center gap-2 text-xs text-[#6B6A66]">
        <span className="flex-1">{titel}</span>
        {zusatz}
      </div>
      {children}
    </div>
  );
}

/** „von Hand" + Zurücksetzen. */
export function VonHand({ aufZurueck }: { aufZurueck?: () => void }) {
  return (
    <span className="flex items-center gap-1">
      <span className="rounded bg-[#E9FF57] px-1.5 py-0.5 text-[10px] font-semibold text-[#1A1A1A]">von Hand</span>
      {aufZurueck && (
        <button type="button" onClick={aufZurueck} title="Zurück auf den gerechneten Wert" className="rounded p-0.5 hover:bg-[#F0F0EC]">
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  );
}

function Stift({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} title="Ändern" className="rounded p-0.5 hover:bg-[#F0F0EC]">
      <Pencil className="h-3.5 w-3.5" />
    </button>
  );
}

function SpeichernLeiste({ speichert, aufSpeichern, aufAbbruch }: {
  speichert: boolean; aufSpeichern: () => void; aufAbbruch: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={aufSpeichern}
        disabled={speichert}
        className="flex items-center gap-1 rounded bg-[#E9FF57] px-3 py-1 text-sm font-semibold text-[#1A1A1A] hover:bg-[#d4ea4d] disabled:opacity-70"
      >
        {speichert && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {speichert ? 'Speichert …' : 'Speichern'}
      </button>
      {!speichert && <button type="button" onClick={aufAbbruch} className="text-xs underline">abbrechen</button>}
    </div>
  );
}

/** Ein Textfeld mit Anzeige / Bearbeiten / von Hand. */
function TextFeld({ titel, wert, vonHand, mehrzeilig, gross, hinweis, aufSpeichern }: {
  titel: string;
  wert: string;
  vonHand: boolean;
  mehrzeilig?: boolean;
  gross?: boolean;
  hinweis?: string;
  aufSpeichern: (neu: string | null) => Promise<void>;
}) {
  const [bearbeitet, setBearbeitet] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const speichern = async (neu: string | null) => {
    setSpeichert(true);
    try { await aufSpeichern(neu); setBearbeitet(null); } finally { setSpeichert(false); }
  };
  return (
    <Karte
      titel={titel}
      zusatz={<>
        {vonHand && <VonHand aufZurueck={() => void speichern(null)} />}
        {bearbeitet == null && <Stift onClick={() => setBearbeitet(wert)} />}
      </>}
    >
      {bearbeitet == null
        ? <p className={`${gross ? 'text-base font-semibold' : 'text-sm'} whitespace-pre-wrap`} title={hinweis}>{wert || '—'}</p>
        : (
          <div className="space-y-2">
            {mehrzeilig
              ? <textarea value={bearbeitet} onChange={e => setBearbeitet(e.target.value)} rows={4} className="w-full rounded border border-[#DAD9D9] p-2 text-sm" />
              : <input value={bearbeitet} onChange={e => setBearbeitet(e.target.value)} className="w-full rounded border border-[#DAD9D9] px-2 py-1 text-sm" />}
            <SpeichernLeiste speichert={speichert} aufSpeichern={() => void speichern(bearbeitet.trim() || null)} aufAbbruch={() => setBearbeitet(null)} />
          </div>
        )}
    </Karte>
  );
}

// ==================== Texte ====================

/**
 * Die Beschreibung (HTML aus der Herstellerdatei) als Textblöcke — ohne sie als HTML
 * einzusetzen. Überschriften, Absätze, Listen.
 */
export function textBloecke(html: string | null): Block[] {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const bloecke: Block[] = [];
  doc.body.childNodes.forEach(n => {
    if (!(n instanceof HTMLElement)) {
      const t = n.textContent?.trim();
      if (t) bloecke.push({ art: 'absatz', zeilen: [t] });
      return;
    }
    if (n.tagName === 'UL' || n.tagName === 'OL') {
      bloecke.push({ art: 'liste', zeilen: Array.from(n.querySelectorAll('li')).map(li => li.textContent?.trim() ?? '').filter(Boolean) });
    } else {
      const t = n.textContent?.trim();
      if (t) bloecke.push({ art: n.querySelector('b,strong,u') && t.length < 60 ? 'titel' : 'absatz', zeilen: [t] });
    }
  });
  return bloecke;
}

function Bloecke({ bloecke }: { bloecke: Block[] }) {
  if (bloecke.length === 0) return <p className="text-sm text-[#6B6A66]">kein Text</p>;
  return <>{bloecke.map((b, i) => b.art === 'liste'
    ? <ul key={i} className="list-disc space-y-1 pl-5 text-sm">{b.zeilen.map((z, j) => <li key={j}>{z}</li>)}</ul>
    : <p key={i} className={`text-sm ${b.art === 'titel' ? 'font-bold underline' : ''}`}>{b.zeilen[0]}</p>)}</>;
}

/** Stand des Gemini-Texts + Knopf zum Erzeugen (Phase 4). */
/**
 * Laufende Anfragen je Artikel — über den Reiterwechsel hinweg. Ohne das startete ein
 * Klick nach dem Zurückkommen denselben Gemini-Aufruf ein zweites Mal (28.09.: Polo Shirt
 * zweimal mit identischer Eingabe).
 */
const inArbeit = new Map<string, Promise<void>>();

function KiZeile({ daten, entwurfId, aufNeuLaden }: { daten: ArtikelPruefung; entwurfId: number; aufNeuLaden: () => void }) {
  const schluessel = `${entwurfId}|${daten.artikelSchluessel}`;
  const [laeuft, setLaeuft] = useState(inArbeit.has(schluessel));
  const [fehler, setFehler] = useState<string | null>(null);
  const erzeugen = async () => {
    const laufend = inArbeit.get(schluessel);
    setLaeuft(true);
    setFehler(null);
    try {
      if (laufend) await laufend;
      else {
        const anfrage = api.post(`/programmes/artikelimport/entwuerfe/${entwurfId}/artikel/texte`, { schluessel: daten.artikelSchluessel })
          .then(() => undefined)
          .finally(() => inArbeit.delete(schluessel));
        inArbeit.set(schluessel, anfrage);
        await anfrage;
      }
      aufNeuLaden();
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? 'Gemini hat nicht geantwortet.');
    } finally { setLaeuft(false); }
  };
  // Läuft beim Zurückkommen noch eine Anfrage, auf sie warten statt eine neue zu starten.
  useEffect(() => {
    if (inArbeit.has(schluessel)) void erzeugen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel]);
  // 🔴 Nur auf Knopfdruck (Matze 28.09.2026: „bitte die Gemini-Texte immer nur auf
  // Knopfdruck … das passiert aktuell zum Teil automatisch, wenn ich einen Artikel
  // anwähle"). Kein automatischer Aufruf beim Öffnen des Reiters.
  if (!daten.ki.aktiv) return <p className="text-xs text-[#6B6A66]">Gemini-Texte sind für diesen Hersteller aus.</p>;
  const { stand, erzeugtAm } = daten.ki;
  const wann = erzeugtAm ? new Date(erzeugtAm).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-lg border px-4 py-2 text-sm ${
      stand === 'aktuell' ? 'border-green-200 bg-green-50 text-green-950'
        : stand === 'veraltet' ? 'border-amber-200 bg-amber-50 text-amber-950' : 'border-[#DAD9D9] bg-white'
    }`}>
      <span className="flex-1">
        {stand === 'aktuell' && `Gemini-Text vom ${wann} — genau dieser Text wird angelegt.`}
        {stand === 'veraltet' && 'Gemini-Text veraltet (Name, Merkmale oder Kategorie geändert) — beim Anlegen schreibt Gemini neu.'}
        {stand === 'fehlt' && (laeuft ? 'Gemini schreibt den Text (meist unter 10 Sekunden) …' : 'Noch kein Gemini-Text — auf Knopfdruck erzeugen.')}
      </span>
      <button
        type="button"
        onClick={() => void erzeugen()}
        disabled={laeuft}
        className="flex items-center gap-2 rounded bg-[#E9FF57] px-3 py-1 text-sm font-semibold text-[#1A1A1A] hover:bg-[#d4ea4d] disabled:opacity-70"
      >
        {laeuft && <Loader2 className="h-4 w-4 animate-spin" />}
        {laeuft ? 'Gemini schreibt …' : stand === 'fehlt' ? 'Jetzt erzeugen' : 'Neu erzeugen'}
      </button>
      {fehler && <span className="w-full text-xs text-red-800">{fehler}</span>}
    </div>
  );
}

export function TexteBearbeiten({ daten, speichere, entwurfId, aufNeuLaden }: {
  daten: ArtikelPruefung; speichere: Speichere; entwurfId: number; aufNeuLaden: () => void;
}) {
  const f = (name: string): SchreibbildFeld | undefined => daten.schreibbild.artikel.find(x => x.feld === name);
  const k = daten.korrektur;
  const bloecke = useMemo(() => textBloecke(daten.schreibbild.beschreibung), [daten.schreibbild.beschreibung]);
  const [text, setText] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const meta = f('Meta-Beschreibung')?.wert.replace(/^\d+ Zeichen: /, '').replace(/^„|"$/g, '') ?? '';
  const gemini = !k.beschreibung && daten.ki.aktiv && daten.ki.stand !== 'aktuell';

  const speichereText = async (neu: string | null) => {
    setSpeichert(true);
    try {
      await speichere('beschreibung', neu == null ? null : bloeckeZuHtml(textZuBloecken(neu)));
      setText(null);
    } finally { setSpeichert(false); }
  };

  return (
    <div className="space-y-3">
    <KiZeile daten={daten} entwurfId={entwurfId} aufNeuLaden={aufNeuLaden} />
    <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-4">
      <Karte
        titel="Artikeltext"
        zusatz={<>
          {k.beschreibung && <VonHand aufZurueck={() => void speichereText(null)} />}
          {text == null && <Stift onClick={() => setText(bloeckeZuText(bloecke))} />}
        </>}
      >
        {text == null ? (
          <>
            {gemini && (
              <div className="rounded border border-dashed border-[#DAD9D9] p-3 text-xs text-[#6B6A66]" title={f('Artikeltext')?.hinweis}>
                [Einleitung — schreibt Gemini beim Anlegen]
              </div>
            )}
            <Bloecke bloecke={bloecke} />
          </>
        ) : (
          <div className="space-y-2">
            <textarea value={text} onChange={e => setText(e.target.value)} rows={14} className="w-full rounded border border-[#DAD9D9] p-2 font-mono text-sm" />
            <p className="text-xs text-[#6B6A66]"># Überschrift · - Listenpunkt · leere Zeile = neuer Absatz. Gemini ändert diesen Text dann nicht mehr.</p>
            <SpeichernLeiste speichert={speichert} aufSpeichern={() => void speichereText(text.trim() || null)} aufAbbruch={() => setText(null)} />
          </div>
        )}
      </Karte>
      <div className="space-y-3">
        <TextFeld titel="Name" gross wert={daten.name} vonHand={!!k.name} aufSpeichern={w => speichere('name', w)} />
        <TextFeld titel="Meta-Beschreibung" mehrzeilig wert={k.metaBeschreibung ?? meta} vonHand={!!k.metaBeschreibung} hinweis={f('Meta-Beschreibung')?.hinweis} aufSpeichern={w => speichere('metaBeschreibung', w)} />
        <TextFeld titel="Keywords" wert={k.keywords ?? f('Meta-Keywords')?.wert ?? ''} vonHand={!!k.keywords} aufSpeichern={w => speichere('keywords', w)} />
        <TextFeld titel="Technische Daten" mehrzeilig wert={k.technischeDaten ?? f('Technische Daten')?.wert ?? ''} vonHand={!!k.technischeDaten} aufSpeichern={w => speichere('technischeDaten', w)} />
        <Karte titel="URL-Pfad"><p className="text-sm">{f('URL-Pfad')?.wert ?? '—'}</p></Karte>
      </div>
    </div>
    </div>
  );
}

// ==================== Preise ====================

const cent = (n: number) => Math.round(n * 100);

/** „Gr. 116–164" bzw. „Gr. S, M" — die Größen einer Preisstufe kurz. */
function groessenSpanne(groessen: string[]): string {
  if (groessen.length === 0) return '';
  if (groessen.length <= 2) return `Gr. ${groessen.join(', ')}`;
  return `Gr. ${groessen[0]}–${groessen[groessen.length - 1]}`;
}

export function PreiseBearbeiten({ daten, speichere }: { daten: ArtikelPruefung; speichere: Speichere }) {
  const korrekturen = daten.korrektur.preise ?? [];
  const [entwurf, setEntwurf] = useState<Record<string, string> | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const schluessel = (preisart: string, wert: number) => `${preisart}|${cent(wert)}`;
  const nachFuer = (preisart: string, von: number) =>
    korrekturen.find(p => p.preisart === preisart && cent(p.von) === cent(von))?.nach;

  const arten = useMemo(() => {
    const karte = new Map<string, typeof daten.preisStufen>();
    for (const s of daten.preisStufen) karte.set(s.preisart, [...(karte.get(s.preisart) ?? []), s]);
    return [...karte.entries()];
  }, [daten.preisStufen]);

  const starte = () => setEntwurf(Object.fromEntries(daten.preisStufen.map(s => [
    schluessel(s.preisart, s.wert), String(nachFuer(s.preisart, s.wert) ?? s.wert).replace('.', ','),
  ])));

  const speichern = async (liste: PreisKorrektur[] | null) => {
    setSpeichert(true);
    try { await speichere('preise', liste && liste.length > 0 ? liste : null); setEntwurf(null); } finally { setSpeichert(false); }
  };
  const ausEntwurf = (): PreisKorrektur[] => daten.preisStufen.flatMap(s => {
    const roh = entwurf?.[schluessel(s.preisart, s.wert)] ?? '';
    const nach = Number(roh.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(nach) && roh.trim() && cent(nach) !== cent(s.wert)
      ? [{ preisart: s.preisart, von: s.wert, nach }] : [];
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <span className="flex flex-1 items-center gap-1 text-sm text-[#1A1A1A]">
          {arten.some(([, stufen]) => stufen.length > 1)
            ? <><b>Größenstaffel:</b>&nbsp;die Preise hängen an der Größe.</>
            : 'Ein Preis für alle Varianten.'}
          <Info><p>Die Preisformel des Herstellers rechnet je Größe (Kindergrößen kosten weniger). Eine Änderung gilt für alle Varianten mit diesem Preis und nur für diesen Artikel.</p></Info>
        </span>
        {korrekturen.length > 0 && <VonHand aufZurueck={() => void speichern(null)} />}
        {entwurf == null && (
          <button type="button" onClick={starte} className="flex items-center gap-1 rounded border border-[#DAD9D9] bg-white px-3 py-1 text-sm">
            <Pencil className="h-3.5 w-3.5" /> Preise ändern
          </button>
        )}
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
        {arten.map(([preisart, stufen]) => (
          <Karte key={preisart} titel={preisart === 'EK' ? 'Einkaufspreis (netto)' : preisart}>
            {stufen.map(s => {
              const nach = nachFuer(s.preisart, s.wert);
              return (
                <div key={s.wert} className="flex items-baseline gap-2">
                  {entwurf == null ? (
                    <span className={`text-lg font-bold tabular-nums ${nach != null ? 'rounded bg-[#E9FF57] px-1' : ''}`}>
                      {euro(nach ?? s.wert)}
                    </span>
                  ) : (
                    <input
                      value={entwurf[schluessel(s.preisart, s.wert)] ?? ''}
                      onChange={e => setEntwurf(v => ({ ...v, [schluessel(s.preisart, s.wert)]: e.target.value }))}
                      className="w-24 rounded border border-[#DAD9D9] px-2 py-1 text-right text-sm tabular-nums"
                    />
                  )}
                  {nach != null && entwurf == null && <span className="text-xs text-[#8A8984] line-through">{euro(s.wert)}</span>}
                  {stufen.length > 1 && (
                    <span className="text-xs text-[#6B6A66]" title={`${zahl(s.varianten)} Varianten: ${s.groessen.join(', ')}`}>
                      {groessenSpanne(s.groessen)}
                    </span>
                  )}
                </div>
              );
            })}
          </Karte>
        ))}
      </div>
      {entwurf != null && (
        <SpeichernLeiste speichert={speichert} aufSpeichern={() => void speichern(ausEntwurf())} aufAbbruch={() => setEntwurf(null)} />
      )}
      {daten.preiseVerwaist.length > 0 && (
        <p className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {zahl(daten.preiseVerwaist.length)} Preisänderung(en) greifen nicht mehr — der gerechnete Preis hat sich geändert
          ({daten.preiseVerwaist.map(p => `${p.preisart} ${euro(p.von)}`).join(', ')}). Bitte neu setzen.
        </p>
      )}
    </div>
  );
}

// ==================== Kategorie, Eigenschaften, Tags ====================

/** Der Plenty-Katalog (Eigenschaften, Tags) — einmal je Seitenaufruf, nicht je Artikel. */
let katalogCache: Promise<KatalogAntwort> | null = null;
function ladeKatalog(): Promise<KatalogAntwort> {
  if (!katalogCache) {
    const laden = api.get<{ data: KatalogAntwort }>('/programmes/artikelimport/katalog')
      .then((a: { data: { data: KatalogAntwort } }) => a.data.data);
    laden.catch(() => { katalogCache = null; });
    katalogCache = laden;
    return laden;
  }
  return katalogCache;
}

type Herkunft = 'maske' | 'geaendert' | 'hier' | 'weg';

export function EigenschaftenBearbeiten({ daten, entwurfId, speichere, aufNeuLaden, aufListeNeu }: {
  daten: ArtikelPruefung; entwurfId: number; speichere: Speichere; aufNeuLaden: () => void;
  /** Die Artikelliste neu laden — die Kategorie-Ampel steht dort (Punkt links). */
  aufListeNeu?: () => void;
}) {
  const [bestaetigt, setBestaetigt] = useState(false);
  const unsicher = daten.ampelKategorie === 'gelb' && daten.kategorien.length > 0;
  const unsicherGrund = daten.hinweise.find(h => h.includes('stützt sich auf'));
  // „passt": die gerechnete Kategorie für diesen Artikel bestätigen — ohne Regel auf den
  // Dateiwert (der ist oft breit, z. B. „Teambekleidung"). Je Hersteller gemerkt.
  const bestaetigen = async () => {
    setBestaetigt(true);
    try {
      await api.patch(`/programmes/artikelimport/entwuerfe/${entwurfId}/kategorie`, {
        artikel: daten.artikelSchluessel, kategorien: daten.kategorien.map(x => x.id), alsRegel: false,
      });
      aufNeuLaden();
      aufListeNeu?.();
    } finally { setBestaetigt(false); }
  };
  const [katalog, setKatalog] = useState<KatalogAntwort | null>(null);
  const [kategorieOffen, setKategorieOffen] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  // Gemini-Kategorie übernehmen (legt die Regel auf den Dateiwert an — so lernt das Tool)
  // oder die Regel behalten (Hand-Entscheidung, Gemini blockiert dann nicht mehr).
  const [kiKatArbeitet, setKiKatArbeitet] = useState<'uebernehmen' | 'behalten' | null>(null);
  const setzeKategorieAusKi = async (kategorien: number[], alsRegel: boolean) => {
    setKiKatArbeitet(alsRegel ? 'uebernehmen' : 'behalten');
    try {
      const r = await api.patch(`/programmes/artikelimport/entwuerfe/${entwurfId}/kategorie`, {
        artikel: daten.artikelSchluessel, kategorien, alsRegel,
      });
      const d = (r as any)?.data?.data ?? (r as any)?.data ?? {};
      setMeldung(d.meldung ?? (alsRegel ? 'Gemini-Kategorie übernommen.' : 'Regel behalten.'));
      aufNeuLaden();
      aufListeNeu?.();
    } catch (e: any) {
      setMeldung(e?.response?.data?.error?.message ?? 'Speichern fehlgeschlagen.');
    } finally { setKiKatArbeitet(null); }
  };
  const [aendert, setAendert] = useState(false);
  useEffect(() => { void ladeKatalog().then(setKatalog).catch(() => setKatalog(null)); }, []);

  const k = daten.korrektur;
  const haupt = daten.schreibbild.varianten.find(v => v.istHaupt) ?? daten.schreibbild.varianten[0];
  const wirksam = new Set((haupt?.eigenschaften ?? []).map(e => e.propertyId));
  const wirksameTags = new Set((haupt?.tags ?? []).map(t => t.tagId));

  // Zeilen: erst die Maske (mit Abweichung), dann was nur hier dazukommt.
  const zeilen = useMemo(() => {
    const korrekturJe = new Map((k.eigenschaften ?? []).map(e => [e.propertyId, e]));
    const liste: Array<{ regel: EigenschaftsRegel; herkunft: Herkunft }> = [];
    for (const r of daten.maske.eigenschaften) {
      const kor = korrekturJe.get(r.propertyId);
      if (!kor) liste.push({ regel: r, herkunft: 'maske' });
      else if (kor.aktion === 'weg') liste.push({ regel: r, herkunft: 'weg' });
      else liste.push({ regel: { propertyId: r.propertyId, auswahlId: kor.auswahlId, wert: kor.wert }, herkunft: 'geaendert' });
    }
    for (const kor of k.eigenschaften ?? []) {
      if (kor.aktion === 'setzen' && !daten.maske.eigenschaften.some(r => r.propertyId === kor.propertyId)) {
        liste.push({ regel: { propertyId: kor.propertyId, auswahlId: kor.auswahlId, wert: kor.wert }, herkunft: 'hier' });
      }
    }
    return liste;
  }, [daten.maske, k.eigenschaften]);

  const speichereEigenschaften = async (neu: EigenschaftsKorrektur[]) => {
    setAendert(true);
    try { await speichere('eigenschaften', neu.length > 0 ? neu : null); } finally { setAendert(false); }
  };
  const ohne = (id: number) => (k.eigenschaften ?? []).filter(e => e.propertyId !== id);
  const setzeWert = (regel: EigenschaftsRegel, teil: Partial<EigenschaftsRegel>) => void speichereEigenschaften([
    ...ohne(regel.propertyId),
    { propertyId: regel.propertyId, aktion: 'setzen', auswahlId: teil.auswahlId ?? regel.auswahlId ?? null, wert: teil.wert ?? regel.wert ?? null },
  ]);

  const tagZeilen = useMemo(() => {
    const weg = new Set(k.tags?.weg ?? []);
    const liste: Array<{ tagId: number; herkunft: Herkunft }> = daten.maske.tags.map(t => ({
      tagId: t.tagId, herkunft: weg.has(t.tagId) ? 'weg' as const : 'maske' as const,
    }));
    for (const id of k.tags?.hinzu ?? []) {
      if (!daten.maske.tags.some(t => t.tagId === id)) liste.push({ tagId: id, herkunft: 'hier' });
    }
    return liste;
  }, [daten.maske, k.tags]);
  const speichereTags = async (hinzu: number[], weg: number[]) => {
    setAendert(true);
    try { await speichere('tags', hinzu.length + weg.length > 0 ? { hinzu, weg } : null); } finally { setAendert(false); }
  };
  const hinzu = k.tags?.hinzu ?? [];
  const weg = k.tags?.weg ?? [];

  const name = (id: number) => katalog?.eigenschaften.find(e => e.id === id)?.name ?? `Eigenschaft ${id}`;
  const tagName = (id: number) => katalog?.tags.find(t => t.id === id)?.name ?? `Tag ${id}`;

  // Gemini klassifiziert ALLE Teambekleidung-Eigenschaften (Datensatz + Hauptbild). Einig
  // mit der Regel = grün und wird geschrieben; nur Gemini = gelb, per Klick; Widerspruch =
  // rot, nichts wird geschrieben, bis hier jemand entscheidet (Matze, 30.09.2026).
  const [kiLaeuft, setKiLaeuft] = useState(false);
  const kiStand = daten.kiEigenschaften;
  const kiWertText = (a: AbgeleiteteEigenschaft, ids: number[]) => ids.map(id => a.auswahl.find(w => w.id === id)?.wert ?? String(id)).join(' + ');
  const frageGemini = async () => {
    setKiLaeuft(true);
    try {
      const r = await api.post(`/programmes/artikelimport/entwuerfe/${entwurfId}/artikel/eigenschaften-ki`, { schluessel: daten.artikelSchluessel });
      const d = (r as any)?.data?.data ?? (r as any)?.data ?? {};
      setRegelMeldung(d.meldung ?? 'Gemini hat geantwortet.');
      aufNeuLaden();
    } catch (e: any) {
      setRegelMeldung(e?.response?.data?.error?.message ?? 'Gemini hat nicht geantwortet.');
    } finally { setKiLaeuft(false); }
  };

  // „für alle mit <Dateiwert> merken" — Regel der Marke plus Korrektur an diesem Artikel.
  const [regelMeldung, setRegelMeldung] = useState<string | null>(null);
  const merkeEigenschaft = async (propertyId: number, auswahlId: number) => {
    setAendert(true);
    try {
      const r = await api.post(`/programmes/artikelimport/entwuerfe/${entwurfId}/eigenschaft-regel`, {
        artikel: daten.artikelSchluessel, propertyId, auswahlId,
      });
      const d = (r as any)?.data?.data ?? (r as any)?.data ?? {};
      await speichere('eigenschaften', [...ohne(propertyId), { propertyId, aktion: 'setzen', auswahlId, wert: null }]);
      setRegelMeldung(`Gemerkt: alle Artikel mit „${d.dateiwert ?? daten.kategorieWert}" bekommen diesen Wert (Regel ${d.regelId ?? ''}). Andere Artikel zeigen ihn nach „Neu lesen".`);
    } catch (e: any) {
      setRegelMeldung(e?.response?.data?.error?.message ?? 'Regel konnte nicht gespeichert werden.');
    } finally { setAendert(false); }
  };

  return (
    <div className="space-y-4">
      <Karte
        titel="Kategorie"
        zusatz={<>{k.kategorieIds && <VonHand />}{!kategorieOffen && <Stift onClick={() => setKategorieOffen(true)} />}</>}
      >
        {daten.kategorien.length > 0
          ? daten.kategorien.map(kat => (
            <div key={kat.id}>
              <div className="text-sm font-semibold">{kat.pfad.split(' > ').join(' › ')}</div>
              <div className="text-xs text-[#6B6A66]">{kat.id}</div>
            </div>
          ))
          : <div className="text-sm text-amber-900">keine</div>}
        {unsicher && !kategorieOffen && (
          <div className="flex flex-wrap items-center gap-2 rounded bg-amber-50 px-3 py-2 text-xs text-amber-950">
            <span className="flex-1">Kategorie bitte prüfen{unsicherGrund ? ' — ' + unsicherGrund.replace(/ — bitte kurz prüfen\.?$/, '') : ''}</span>
            <button
              type="button"
              onClick={() => void bestaetigen()}
              disabled={bestaetigt}
              className="flex items-center gap-1 rounded bg-[#E9FF57] px-2.5 py-1 font-semibold text-[#1A1A1A] hover:bg-[#d4ea4d] disabled:opacity-70"
            >
              {bestaetigt && <Loader2 className="h-3.5 w-3.5 animate-spin" />}passt
            </button>
            <button type="button" onClick={() => setKategorieOffen(true)} className="underline">ändern</button>
          </div>
        )}
        {daten.kategorieKi && !kategorieOffen && (
          <KategorieKiZeile
            ki={daten.kategorieKi}
            arbeitet={kiKatArbeitet}
            aufUebernehmen={() => void setzeKategorieAusKi(daten.kategorieKi!.vorschlag.map(x => x.id), true)}
            aufBehalten={() => void setzeKategorieAusKi(daten.kategorien.map(x => x.id), false)}
          />
        )}
        {meldung && <p className="text-xs text-green-800">{meldung}</p>}
        {kategorieOffen && (
          <KategorieWahl
            entwurfId={entwurfId}
            artikelSchluessel={daten.artikelSchluessel}
            artikelName={daten.name}
            dateiwert={daten.kategorieWert}
            aktuell={daten.kategorien.map(x => x.id)}
            aufGespeichert={m => { setMeldung(m); setKategorieOffen(false); aufNeuLaden(); aufListeNeu?.(); }}
            aufAbbruch={() => setKategorieOffen(false)}
          />
        )}
      </Karte>

      <Karte
        titel="Teambekleidung-Eigenschaften · Gemini entscheidet"
        zusatz={<>
          {kiStand?.vorhanden && !kiLaeuft && (
            <span className="text-xs text-[#6B6A66]" title={`Gemini ${kiStand.modell}${kiStand.mitBild ? ' mit Hauptbild' : ' ohne Bild'}`}>
              Gemini geprüft · {usd(kiStand.kostenUsd)}
            </span>
          )}
          <button type="button" onClick={() => void frageGemini()} disabled={kiLaeuft}
            title={`Ein Aufruf an ${kiStand?.modell ?? 'Gemini'} mit Datensatz${kiStand?.mitBild || (daten.alleBilder?.length ?? 0) > 0 ? ' und Hauptbild' : ''}`}
            className="flex items-center gap-1 rounded bg-[#E9FF57] px-2.5 py-1 text-xs font-semibold text-[#1A1A1A] hover:bg-[#d4ea4d] disabled:opacity-70">
            {kiLaeuft && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {kiLaeuft ? 'Gemini prüft Datensatz und Bild …' : `${kiStand?.vorhanden ? 'Gemini erneut fragen' : 'Gemini fragen'} (≈ ${usd(kiStand?.schaetzungUsd)})`}
          </button>
          <Info>Gemini bekommt den ganzen Datensatz plus Hauptbild und entscheidet alle Eigenschaften der Gruppe — auch die Teamline. Was Gemini setzt, wird geschrieben; weicht es von der Regel ab, steht die Regel als Hinweis daneben. „Trifft nicht zu“ streicht die Eigenschaft (eingeklappt unten). Regeln gelten nur als Vorbelegung für Artikel, die Gemini noch nicht gesehen hat. Deine Änderung hier gewinnt über alles und gilt nur für diesen Artikel; „merken“ macht daraus eine Regel für alle mit demselben Dateiwert. Mehrfachauswahl per Kästchen wie in Plenty. Kosten je Aufruf am Knopf, der echte Betrag danach daneben.</Info>
        </>}
      >
        {(daten.abgeleitet ?? []).length === 0 && (
          <p className="text-xs text-[#6B6A66]">Keine Teambekleidung-Eigenschaft zuständig. „Gemini fragen“ prüft trotzdem alle — ein Shirt in „Shirts“ kann so „Trikots = Kurzarm“ bekommen.</p>
        )}
        {(daten.abgeleitet ?? []).filter(a => !(a.kiStatus === 'nichtZutreffend' && (k.eigenschaften ?? []).every(e => e.propertyId !== a.propertyId))).map(a => {
          const kor = (k.eigenschaften ?? []).find(e => e.propertyId === a.propertyId);
          const vonHand = kor?.aktion === 'setzen';
          const weg = kor?.aktion === 'weg';
          const mehrfach = a.cast === 'multiSelection';
          const status = a.kiStatus ?? 'ohneKi';
          // Was gilt (wird geschrieben): Hand > Gemini > Regel.
          const gesetzt: number[] = vonHand
            ? (kor.auswahlIds && kor.auswahlIds.length > 0 ? kor.auswahlIds : (kor.auswahlId != null ? [kor.auswahlId] : []))
            : a.auswahlIds;
          const punkt = weg ? 'bg-[#DAD9D9]' : vonHand ? 'bg-[#1A1A1A]' : a.ampel === 'gruen' ? 'bg-green-600' : 'bg-amber-500';
          const setzen = (ids: number[]) => void speichereEigenschaften(
            ids.length === 0
              ? [...ohne(a.propertyId), { propertyId: a.propertyId, aktion: 'weg' }]
              : [...ohne(a.propertyId), { propertyId: a.propertyId, aktion: 'setzen', auswahlId: ids[0], wert: null, ...(mehrfach ? { auswahlIds: ids } : {}) }],
          );
          return (
            <div key={a.propertyId} className={`flex flex-wrap items-center gap-2 rounded border border-[#ECECE8] p-2 ${weg ? 'opacity-50' : ''}`}>
              <span className={`inline-block h-2.5 w-2.5 rounded-full ${punkt}`} title={vonHand ? 'von Hand' : a.grund} />
              <span className={`min-w-[10rem] flex-1 text-sm ${weg ? 'line-through' : ''}`}>
                {a.name}<span className="ml-2 text-xs text-[#8A8984]">{a.propertyId}</span>
              </span>
              {vonHand && <VonHand />}

              {/* Herkunft der Entscheidung */}
              {!vonHand && !weg && status === 'einig' && (
                <span className="rounded bg-green-50 px-2 py-0.5 text-xs text-green-900">Regel + Gemini einig</span>
              )}
              {!vonHand && !weg && status === 'gemini' && (
                <span className="flex items-center gap-1 text-xs">
                  <span className="rounded bg-green-50 px-2 py-0.5 text-green-900">Gemini</span>
                  {(a.regelAuswahlIds?.length ?? 0) > 0 && (
                    <span className="rounded bg-amber-50 px-2 py-0.5 text-amber-900" title="Gemini hat anders entschieden als die Regel — Gemini gilt">
                      Regel sagte {kiWertText(a, a.regelAuswahlIds ?? [])}
                    </span>
                  )}
                  {a.regelIds.length === 0 && (a.regelAuswahlIds?.length ?? 0) === 0 && <span className="text-[#6B6A66]">laut Regeln nicht zuständig</span>}
                </span>
              )}
              {!vonHand && !weg && status === 'nurRegel' && (
                <span className="rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-900">
                  Gemini unsicher · {a.sicherheit === 'offen' ? 'bitte wählen' : 'Regelwert gilt'}
                </span>
              )}
              {!vonHand && !weg && status === 'ohneKi' && (
                <span className={`rounded px-2 py-0.5 text-xs ${a.sicherheit === 'sicher' ? 'bg-[#F0F0EC] text-[#6B6A66]' : 'bg-amber-50 text-amber-900'}`}>
                  {a.sicherheit === 'offen' ? 'bitte wählen' : a.sicherheit === 'angenommen' ? 'angenommen' : 'Regel'} · Gemini noch nicht gefragt
                </span>
              )}
              {!vonHand && !weg && status === 'nichtZutreffend' && (
                <span className="rounded border border-[#DAD9D9] px-2 py-0.5 text-xs text-[#6B6A66]">Gemini: trifft nicht zu</span>
              )}

              {/* Eingabe: Kästchen bei Mehrfachauswahl (wie in Plenty), sonst Dropdown */}
              {!weg && mehrfach && (
                <span className="flex flex-wrap items-center gap-2 rounded border border-[#DAD9D9] bg-white px-2 py-1">
                  {a.auswahl.map(w => (
                    <label key={w.id} className="flex items-center gap-1 text-sm">
                      <input
                        type="checkbox"
                        checked={gesetzt.includes(w.id)}
                        onChange={ev => setzen(ev.target.checked ? Array.from(new Set([...gesetzt, w.id])) : gesetzt.filter(id => id !== w.id))}
                      />
                      {w.wert}
                    </label>
                  ))}
                </span>
              )}
              {!weg && !mehrfach && (
                <select
                  value={gesetzt[0] ?? ''}
                  onChange={ev => { const id = Number(ev.target.value); if (id) setzen([id]); }}
                  className="rounded border border-[#DAD9D9] bg-white px-2 py-1 text-sm"
                >
                  <option value="">– wählen –</option>
                  {a.auswahl.map(w => <option key={w.id} value={w.id}>{w.wert}</option>)}
                </select>
              )}
              {!weg && !mehrfach && gesetzt.length === 1 && daten.kategorieWert && (
                <button
                  type="button"
                  title={`Regel: alle Artikel mit „${daten.kategorieWert}" bekommen ${a.name} = ${a.auswahl.find(w => w.id === gesetzt[0])?.wert ?? gesetzt[0]}`}
                  onClick={() => void merkeEigenschaft(a.propertyId, gesetzt[0])}
                  className="rounded border border-[#DAD9D9] px-2 py-1 text-xs hover:bg-[#F0F0EC]"
                >für alle mit „{daten.kategorieWert}“ merken</button>
              )}
              {vonHand || weg
                ? <button type="button" title="Wie gerechnet" onClick={() => void speichereEigenschaften(ohne(a.propertyId))} className="rounded p-1 hover:bg-[#F0F0EC]"><RotateCcw className="h-4 w-4" /></button>
                : <button type="button" title="Bei diesem Artikel weglassen" onClick={() => setzen([])} className="rounded p-1 text-[#6B6A66] hover:bg-[#F0F0EC] hover:text-red-700"><Trash2 className="h-4 w-4" /></button>}
            </div>
          );
        })}
        {(() => {
          const gestrichen = (daten.abgeleitet ?? []).filter(a => a.kiStatus === 'nichtZutreffend' && (k.eigenschaften ?? []).every(e => e.propertyId !== a.propertyId));
          if (gestrichen.length === 0) return null;
          return (
            <details className="rounded border border-dashed border-[#DAD9D9] px-2 py-1 text-xs text-[#6B6A66]">
              <summary className="cursor-pointer">
                Laut Gemini nicht zutreffend, wird nicht geschrieben: {gestrichen.map(a => a.name).join(', ')}
                {gestrichen.some(a => (a.regelAuswahlIds?.length ?? 0) > 0) && ' · darunter Regelwerte, die Gemini gestrichen hat'}
              </summary>
              <div className="mt-1 space-y-1">
                {gestrichen.map(a => (
                  <div key={a.propertyId} className="flex flex-wrap items-center gap-2">
                    <span>{a.name}{(a.regelAuswahlIds?.length ?? 0) > 0 && <span className="ml-1 text-amber-900">(Regel sagte {kiWertText(a, a.regelAuswahlIds ?? [])})</span>}</span>
                    {a.auswahl.length > 0 && a.cast !== 'multiSelection' && (
                      <select defaultValue="" onChange={ev => { const id = Number(ev.target.value); if (id) void speichereEigenschaften([...ohne(a.propertyId), { propertyId: a.propertyId, aktion: 'setzen', auswahlId: id, wert: null }]); }}
                        className="rounded border border-[#DAD9D9] bg-white px-1 py-0.5 text-xs">
                        <option value="">trotzdem setzen …</option>
                        {a.auswahl.map(w => <option key={w.id} value={w.id}>{w.wert}</option>)}
                      </select>
                    )}
                    {a.auswahl.length > 0 && a.cast === 'multiSelection' && a.auswahl.map(w => (
                      <button key={w.id} type="button" onClick={() => void speichereEigenschaften([...ohne(a.propertyId), { propertyId: a.propertyId, aktion: 'setzen', auswahlId: w.id, wert: null, auswahlIds: [w.id] }])}
                        className="rounded border border-[#DAD9D9] px-1.5 py-0.5 hover:bg-[#F0F0EC]">+ {w.wert}</button>
                    ))}
                  </div>
                ))}
              </div>
            </details>
          );
        })()}
        {regelMeldung && <p className="text-xs text-green-800">{regelMeldung}</p>}
      </Karte>

      {daten.druck && (
        <Karte titel="Druck-Eigenschaften · nur Oberteile (Teambekleidung = Oberteil)">
          <p className="text-sm">
            {daten.druck.oberteil
              ? `Ja — ${daten.druck.eigenschaften} Druck-Bestelleigenschaften an jeder Variante (Spielername, Rückennummer, Initialen, Teamname, Wappen, Sponsoren …).`
              : 'Nein — kein Oberteil, der Artikel bekommt keine Druck-Eigenschaften.'}
          </p>
          {daten.druck.warnung && (
            <p className="mt-1 flex items-start gap-1 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{daten.druck.warnung}
            </p>
          )}
        </Karte>
      )}

      <Karte titel="Eigenschaften · Änderungen gelten nur für diesen Artikel" zusatz={aendert && <Loader2 className="h-3.5 w-3.5 animate-spin" />}>
        {zeilen.map(({ regel, herkunft }) => {
          const eigenschaft = katalog?.eigenschaften.find(e => e.id === regel.propertyId);
          const greiftNicht = herkunft === 'maske' && !wirksam.has(regel.propertyId);
          return (
            <div key={regel.propertyId} className={`flex flex-wrap items-center gap-2 rounded border border-[#ECECE8] p-2 ${herkunft === 'weg' ? 'opacity-50' : ''}`}>
              <span className={`min-w-[12rem] flex-1 text-sm ${herkunft === 'weg' ? 'line-through' : ''}`}>
                {name(regel.propertyId)}
                <span className="ml-2 text-xs text-[#8A8984]">{regel.propertyId}</span>
              </span>
              <HerkunftMarke herkunft={herkunft} greiftNicht={greiftNicht} />
              {herkunft !== 'weg' && (
                <WertEingabe cast={eigenschaft?.cast} auswahl={eigenschaft?.auswahl ?? []} regel={regel} aufSpeichern={teil => setzeWert(regel, teil)} />
              )}
              {herkunft === 'weg' || herkunft === 'geaendert'
                ? <button type="button" title="Wie für alle" onClick={() => void speichereEigenschaften(ohne(regel.propertyId))} className="rounded p-1 hover:bg-[#F0F0EC]"><RotateCcw className="h-4 w-4" /></button>
                : <button
                  type="button"
                  title={herkunft === 'hier' ? 'Entfernen' : 'Bei diesem Artikel weglassen'}
                  onClick={() => void speichereEigenschaften(herkunft === 'hier' ? ohne(regel.propertyId) : [...ohne(regel.propertyId), { propertyId: regel.propertyId, aktion: 'weg' }])}
                  className="rounded p-1 text-[#6B6A66] hover:bg-[#F0F0EC] hover:text-red-700"
                ><Trash2 className="h-4 w-4" /></button>}
            </div>
          );
        })}
        {katalog && (
          <Hinzufuegen
            platzhalter="Eigenschaft nur hier hinzufügen …"
            eintraege={katalog.eigenschaften
              .filter(e => e.schreibbar && e.id !== KAUFLAND_KATEGORIE_ID && !zeilen.some(z => z.regel.propertyId === e.id))
              .map(e => ({ id: e.id, name: e.name, zusatz: e.cast }))}
            aufWahl={id => void speichereEigenschaften([...ohne(id), { propertyId: id, aktion: 'setzen', auswahlId: null, wert: null }])}
          />
        )}
      </Karte>

      {daten.kaufland && <KauflandKarte kaufland={daten.kaufland} speichere={speichere} />}

      <Karte titel="Tags · Änderungen gelten nur für diesen Artikel">
        <div className="flex flex-wrap gap-2">
          {tagZeilen.map(({ tagId, herkunft }) => (
            <span key={tagId} className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs ${
              herkunft === 'weg' ? 'border-[#DAD9D9] text-[#8A8984] line-through'
                : herkunft === 'hier' ? 'border-[#1A1A1A] bg-[#E9FF57]' : 'border-[#DAD9D9] bg-[#F0F0EC]'
            }`} title={herkunft === 'maske' && !wirksameTags.has(tagId) ? 'Greift bei diesem Artikel nicht (Bedingung der Maske)' : undefined}>
              {tagName(tagId)}
              {herkunft === 'weg'
                ? <button type="button" title="Wie für alle" onClick={() => void speichereTags(hinzu, weg.filter(x => x !== tagId))}><RotateCcw className="h-3 w-3" /></button>
                : <button type="button" title="Bei diesem Artikel weglassen" onClick={() => void speichereTags(
                  hinzu.filter(x => x !== tagId), herkunft === 'hier' ? weg : [...weg, tagId],
                )}><Trash2 className="h-3 w-3" /></button>}
            </span>
          ))}
        </div>
        {katalog && (
          <Hinzufuegen
            platzhalter="Tag nur hier hinzufügen …"
            eintraege={katalog.tags.filter(t => !tagZeilen.some(z => z.tagId === t.id)).map(t => ({ id: t.id, name: t.name }))}
            aufWahl={id => void speichereTags([...hinzu, id], weg)}
          />
        )}
      </Karte>
    </div>
  );
}

/**
 * Auswahl-Eigenschaften speichern sofort; Text und Zahl erst auf „übernehmen" — sonst
 * wäre jeder Tastendruck ein Speichervorgang.
 */
const KI_KAT_TEXT: Record<KategorieKiStatus, string> = {
  einig: 'Regel + Gemini einig',
  vorschlag: 'nur Gemini',
  widerspruch: 'Gemini anders',
  nurRegel: 'Gemini unsicher',
  offen: 'offen',
  keineLinie: 'keine Linie',
};
const KI_KAT_FARBE: Record<KategorieKiStatus, string> = {
  einig: 'bg-green-50 text-green-800',
  vorschlag: 'bg-amber-50 text-amber-900',
  widerspruch: 'bg-red-50 text-red-800',
  nurRegel: 'bg-[#F4F3F1] text-[#6B6A66]',
  offen: 'bg-red-50 text-red-800',
  keineLinie: 'bg-[#F4F3F1] text-[#6B6A66]',
};

/**
 * Was Gemini zur Kategorie sagt (07.10.2026) — Produkt und Teamline je eine Zeile. Bei
 * „nur Gemini" oder Widerspruch wird der Artikel nicht angelegt, bis hier jemand klickt.
 */
function KategorieKiZeile({ ki, arbeitet, aufUebernehmen, aufBehalten }: {
  ki: KategorieKi;
  arbeitet: 'uebernehmen' | 'behalten' | null;
  aufUebernehmen: () => void;
  aufBehalten: () => void;
}) {
  const kurz = (pfad: string) => pfad.split(' > ').slice(-2).join(' › ');
  const zeile = (titel: string, status: KategorieKiStatus, text: string) => (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-28 text-[#6B6A66]">{titel}</span>
      <span className="flex-1">{text}</span>
      <span className={`rounded px-1.5 py-0.5 ${KI_KAT_FARBE[status]}`}>{KI_KAT_TEXT[status]}</span>
    </div>
  );
  return (
    <div className="space-y-1.5 rounded border border-[#DAD9D9] px-3 py-2">
      <div className="text-xs font-semibold">Gemini{ki.vonHand ? ' · von Hand entschieden' : ''}</div>
      {zeile('Produktkategorie', ki.produkt.status, ki.produkt.gemini ? kurz(ki.produkt.gemini.pfad) : 'unsicher')}
      {zeile('Teamline', ki.teamline.status, ki.teamline.gemini ? kurz(ki.teamline.gemini.pfad) : ki.teamline.keineLinie ? 'keine Linie' : 'unsicher')}
      {ki.blockiert && (
        <div className="flex flex-wrap items-center gap-2 rounded bg-red-50 px-2 py-1.5 text-xs text-red-900">
          <span className="flex-1">{ki.grund} Ohne Entscheidung wird der Artikel nicht angelegt.</span>
          {ki.vorschlag.length > 0 && (
            <button type="button" onClick={aufUebernehmen} disabled={arbeitet != null}
              title={`Setzt ${ki.vorschlag.map(v => kurz(v.pfad)).join(' + ')} und merkt die Produktkategorie als Regel für den Dateiwert`}
              className="flex items-center gap-1 rounded bg-[#E9FF57] px-2.5 py-1 font-semibold text-[#1A1A1A] hover:bg-[#d4ea4d] disabled:opacity-70">
              {arbeitet === 'uebernehmen' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {arbeitet === 'uebernehmen' ? 'Übernehme Gemini …' : 'Gemini übernehmen + merken'}
            </button>
          )}
          <button type="button" onClick={aufBehalten} disabled={arbeitet != null}
            className="flex items-center gap-1 underline disabled:opacity-70">
            {arbeitet === 'behalten' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {arbeitet === 'behalten' ? 'Speichere …' : 'Regel behalten'}
          </button>
        </div>
      )}
    </div>
  );
}

function WertEingabe({ cast, auswahl, regel, aufSpeichern }: {
  cast?: string;
  auswahl: Array<{ id: number; wert: string }>;
  regel: EigenschaftsRegel;
  aufSpeichern: (teil: Partial<EigenschaftsRegel>) => void;
}) {
  const [lokal, setLokal] = useState<EigenschaftsRegel>(regel);
  useEffect(() => { setLokal(regel); }, [regel]);
  const sofort = cast === 'selection' || cast === 'multiSelection';
  const geaendert = (lokal.wert ?? '') !== (regel.wert ?? '');
  return (
    <span className="flex items-center gap-2">
      <Wertfeld
        cast={cast}
        auswahl={auswahl}
        regel={lokal}
        aufAenderung={teil => { setLokal(v => ({ ...v, ...teil })); if (sofort) aufSpeichern(teil); }}
      />
      {!sofort && geaendert && (
        <button type="button" onClick={() => aufSpeichern({ wert: lokal.wert })} className="rounded bg-[#E9FF57] px-2 py-1 text-xs font-semibold text-[#1A1A1A]">
          übernehmen
        </button>
      )}
    </span>
  );
}

function HerkunftMarke({ herkunft, greiftNicht }: { herkunft: Herkunft; greiftNicht: boolean }) {
  if (herkunft === 'maske') {
    return <span className="text-xs text-[#8A8984]">{greiftNicht ? 'für alle · greift hier nicht' : 'für alle'}</span>;
  }
  const text = herkunft === 'geaendert' ? 'nur hier anders' : herkunft === 'hier' ? 'nur hier' : 'hier weggelassen';
  return <span className="rounded bg-[#E9FF57] px-1.5 py-0.5 text-[10px] font-semibold text-[#1A1A1A]">{text}</span>;
}

// ==================== Bilder ====================

export function BilderBearbeiten({ daten, speichere, bildUrl }: {
  daten: ArtikelPruefung;
  speichere: Speichere;
  bildUrl: (b: { datei: string; ordner: string }) => string;
}) {
  const [aendert, setAendert] = useState(false);
  const k = daten.korrektur.bilder ?? { weg: [], hauptbild: null };
  const infos = useMemo(() => {
    const karte = new Map<string, { farbe: string; varianten: number }>();
    for (const v of daten.schreibbild.varianten) {
      for (const b of v.bilder) {
        const farbe = v.attribute.find(a => a.rolle === 'farbe')?.wert ?? '';
        const e = karte.get(b.datei);
        if (e) { e.varianten++; if (!e.farbe) e.farbe = farbe; } else karte.set(b.datei, { farbe, varianten: 1 });
      }
    }
    return karte;
  }, [daten.schreibbild.varianten]);
  const aktiveReihenfolge = useMemo(() => {
    const aktiv = new Set(infos.keys());
    const hauptbild = daten.schreibbild.varianten.flatMap(v => v.bilder).find(b => b.hauptbild)?.datei;
    return { aktiv, hauptbild };
  }, [infos, daten.schreibbild.varianten]);

  const setze = async (neu: { weg: string[]; hauptbild?: string | null }) => {
    setAendert(true);
    try { await speichere('bilder', neu.weg.length > 0 || neu.hauptbild ? neu : null); } finally { setAendert(false); }
  };

  if (daten.alleBilder.length === 0) return <p className="text-sm text-amber-900">Kein Bild gefunden — der Artikel entsteht ohne.</p>;
  const geaendert = k.weg.length > 0 || !!k.hauptbild;

  return (
    <>
      <div className="flex items-center gap-3 text-xs text-[#6B6A66]">
        <span className="flex-1">
          {zahl(aktiveReihenfolge.aktiv.size)} von {zahl(daten.alleBilder.length)} Bildern gehen mit
          {daten.bildOrdner.length > 0 && ` · aus ${daten.bildOrdner.join(' · ')}`}
        </span>
        {aendert && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {geaendert && <VonHand aufZurueck={() => void setze({ weg: [] })} />}
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4">
        {daten.alleBilder.map(b => {
          const info = infos.get(b.datei);
          const aus = k.weg.includes(b.datei) || !info;
          const istHaupt = aktiveReihenfolge.hauptbild === b.datei;
          return (
            <div key={b.datei} className={`overflow-hidden rounded-lg border bg-white ${aus ? 'border-dashed border-[#DAD9D9] opacity-50' : 'border-[#DAD9D9]'}`}>
              <a href={bildUrl(b)} target="_blank" rel="noreferrer" className="relative block h-44 bg-[#F7F7F4]" title="Groß öffnen">
                <img src={bildUrl(b)} alt={b.datei} loading="lazy" className="h-full w-full object-contain p-2" />
                {istHaupt && !aus && <span className="absolute left-2 top-2 rounded bg-[#1A1A1A] px-2 py-0.5 text-xs font-semibold text-white">Hauptbild</span>}
              </a>
              <div className="space-y-1 p-3">
                <div className="text-sm font-medium">{info?.farbe || (aus ? 'nicht verwendet' : 'Hauptvariante')}</div>
                <div className="text-xs text-[#6B6A66]">{b.datei}{info && ` · an ${zahl(info.varianten)} Varianten`}</div>
                <div className="flex gap-3 pt-1 text-xs">
                  {aus
                    ? <button type="button" className="flex items-center gap-1 underline" onClick={() => void setze({ ...k, weg: k.weg.filter(d => d !== b.datei) })}><Eye className="h-3 w-3" /> verwenden</button>
                    : <button type="button" className="flex items-center gap-1 underline" onClick={() => void setze({ weg: [...k.weg, b.datei], hauptbild: k.hauptbild === b.datei ? null : k.hauptbild })}><EyeOff className="h-3 w-3" /> weglassen</button>}
                  {!aus && !istHaupt && (
                    <button type="button" className="flex items-center gap-1 underline" onClick={() => void setze({ ...k, hauptbild: b.datei })}><Star className="h-3 w-3" /> als Hauptbild</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
